import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { MailIcon, PhoneIcon } from "./ContactIcons";
import { useLang } from "../context/LanguageContext";
import { FadeUp } from "./Reveal";
import { WHATSAPP_PRIMARY, WHATSAPP_SECONDARY, waLink, LEGAL_NAME, LICENSE_NUMBER, ADDRESS, INSTAGRAM_URL, TIKTOK_URL } from "../siteConfig";

const NAV = [
  { key: "home", to: "/" },
  { key: "cars", to: "/cars" },
  { key: "dealers", to: "/dealers" },
  { key: "private", to: "/private-clients" },
  { key: "sell", to: "/sell-your-car" },
  { key: "process", to: "/our-process" },
  { key: "network", to: "/global-network" },
  { key: "about", to: "/about" },
  { key: "contact", to: "/contact" },
];

const LEGAL = [
  { key: "privacy", to: "/privacy-policy" },
  { key: "terms", to: "/terms" },
  { key: "cookies", to: "/cookie-policy" },
];

export default function Footer() {
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <footer className="bg-[#050607] border-t border-white/10" data-testid="site-footer">
      <div className="px-5 md:px-10 pt-16 md:pt-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pb-16">
          <FadeUp>
            <div className="eyebrow mb-5">{t("footer.navTitle")}</div>
            <ul className="space-y-2.5">
              {NAV.map((n) => (
                <li key={n.key}>
                  <Link to={n.to} data-testid={`footer-link-${n.key}`} className="mono text-[12px] text-[#999995] hover:text-[#F3F3F0] transition-colors">
                    {t(`nav.${n.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.05}>
            <div className="eyebrow mb-5">{t("footer.legalTitle")}</div>
            <ul className="space-y-2.5">
              {LEGAL.map((n) => (
                <li key={n.key}>
                  <Link to={n.to} data-testid={`footer-link-${n.key}`} className="mono text-[12px] text-[#999995] hover:text-[#F3F3F0] transition-colors">
                    {t(`footer.${n.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="eyebrow mb-5">{t("footer.contactTitle")}</div>
            <ul className="space-y-2.5">
              <li>
                <a href="mailto:info@veyrone.es" data-testid="footer-email" className="flex items-center gap-2.5 mono text-[12px] text-[#999995] hover:text-[#F3F3F0] transition-colors w-fit py-1">
                  <span className="text-[#D1C7BD]"><MailIcon size={14} /></span>
                  info@veyrone.es
                </a>
              </li>
              <li>
                <a href={waLink(WHATSAPP_PRIMARY, t("whatsapp.msg"))} target="_blank" rel="noopener noreferrer" data-testid="footer-phone-1" className="w-fit py-1 block">
                  <span className="field-label !text-[9px] !mb-1">{t("nx.contact.spainOffice")}</span>
                  <span className="flex items-center gap-2.5 mono text-[12px] text-[#999995] hover:text-[#F3F3F0] transition-colors">
                    <span className="text-[#D1C7BD]"><PhoneIcon size={14} /></span>
                    +34 622 181 686
                  </span>
                </a>
              </li>
              <li>
                <a href={waLink(WHATSAPP_SECONDARY, t("whatsapp.msg"))} target="_blank" rel="noopener noreferrer" data-testid="footer-phone-2" className="w-fit py-1 block">
                  <span className="field-label !text-[9px] !mb-1">{t("nx.contact.dubaiOffice")}</span>
                  <span className="flex items-center gap-2.5 mono text-[12px] text-[#999995] hover:text-[#F3F3F0] transition-colors">
                    <span className="text-[#D1C7BD]"><PhoneIcon size={14} /></span>
                    +34 639 341 794
                  </span>
                </a>
              </li>
            </ul>
            <div className="mono text-[10px] text-[#666663] leading-relaxed mt-6 space-y-1" data-testid="footer-legal-info">
              <div>{LEGAL_NAME}</div>
              <div>{t("nx.contact.licence")}: {LICENSE_NUMBER}</div>
              <div>{ADDRESS}</div>
            </div>
            {(INSTAGRAM_URL || TIKTOK_URL) && (
              <div className="flex gap-4 mt-6" data-testid="footer-socials">
                {INSTAGRAM_URL && (
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="text-[#666663] hover:text-[#F3F3F0] transition-colors">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.4" /><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.4" /><circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" /></svg>
                  </a>
                )}
                {TIKTOK_URL && (
                  <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok" className="text-[#666663] hover:text-[#F3F3F0] transition-colors">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M14.5 3v10.6a3.4 3.4 0 1 1-3.4-3.4h.6v2.6h-.6a.8.8 0 1 0 .8.8V3h2.6a5.9 5.9 0 0 0 3.4 5v2.6A8.5 8.5 0 0 1 14.5 3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></svg>
                  </a>
                )}
              </div>
            )}
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="eyebrow mb-5">{t("footer.langTitle")}</div>
            <FooterLangs />
            <div className="mono text-[12px] text-[#666663] mt-8 tracking-[0.2em]">{t("footer.tagline")}</div>
          </FadeUp>
        </div>

        <div className="hairline-t pt-6 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <Link to="/" onClick={goHome} data-testid="footer-logo-link" className="p-2 -m-2 flex items-center self-start md:self-auto"><Logo size={16} /></Link>
          <div className="eyebrow !text-[10px]">{t("footer.rights")}</div>
        </div>

        <FadeUp y={16}>
          <div className="overflow-hidden" data-testid="footer-wordmark">
            <div
              className="wordmark text-center leading-none text-[#0F1012] select-none whitespace-nowrap"
              style={{ fontSize: "clamp(2.6rem, 12.5vw, 13rem)", letterSpacing: "0.22em", marginRight: "-0.22em", marginBottom: "-0.1em" }}
            >
              VEYRONE
            </div>
          </div>
        </FadeUp>
      </div>
    </footer>
  );
}

function FooterLangs() {
  const { lang, setLang, langs } = useLang();
  return (
    <div className="flex gap-3">
      {langs.map((l) => (
        <button
          key={l.code}
          data-testid={`footer-lang-${l.code.toLowerCase()}`}
          onClick={() => setLang(l.code)}
          className={`mono text-[12px] transition-colors ${lang === l.code ? "text-[#F3F3F0]" : "text-[#666663] hover:text-[#999995]"}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}
